import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { FileText, Users, Menu, X, LogOut, Shield, Settings, FolderTree, ChevronDown, Download, MessageSquare, Headphones, Home, ArrowLeft, ClipboardList, Mail, GraduationCap, Bell, Upload, Plus, Trash2, CreditCard, Package, Inbox, Crown } from 'lucide-react';
import { motion } from 'framer-motion';
import { supabase } from '../../supabase/client';

interface AdminUser {
  id: string;
  username: string;
  role: 'super' | 'normal';
  createdAt: string | null;
}

interface HomepageMenuItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  children?: MenuChildItem[];
}

const homepageMenuGroup: { title: string; items: HomepageMenuItem[] } = {
  title: '首页管理',
  items: [
    { path: '/admin/notices', label: '公告栏', icon: Bell },
    { path: '/admin/downloads', label: '下载中心', icon: Download },
    { path: '/admin/nav-items', label: '导航栏', icon: Menu },
    { path: '/admin/customer-service', label: '客服设置', icon: Headphones },
    { path: '/admin/footer-settings', label: '页脚设置', icon: FileText },
  ]
};

const messageMenuGroup: { title: string; items: HomepageMenuItem[] } = {
  title: '站内信管理',
  items: [
    { path: '/admin/message-config', label: '场景配置', icon: Settings },
    { path: '/admin/message-templates', label: '模板管理', icon: FileText },
    { path: '/admin/send-message', label: '发送消息', icon: MessageSquare },
    { path: '/admin/message-test', label: '发送测试', icon: Mail },
    { path: '/admin/message-statistics', label: '发送统计', icon: ClipboardList },
  ]
};



const paperMenuItems = [
  { path: '/admin/papers', label: '试卷列表', icon: FileText },
  { path: '/admin/pending-papers', label: '草稿列表', icon: ClipboardList },
  { path: '/admin/paper-upload', label: '试卷上传', icon: Upload },
  { path: '/admin/paper-editor', label: '试卷编辑', icon: Plus },
  { path: '/admin/answer-settings', label: '答题设置', icon: Settings },
  { path: '/admin/recycle-bin', label: '回收站', icon: Trash2 },
];

const businessMenuItems = [
  { path: '/admin/members', label: '学员列表', icon: Users },
  { path: '/admin/teachers', label: '教师列表', icon: Users },
  { path: '/admin/student-orders', label: '学员订单', icon: ClipboardList },
  { path: '/admin/teacher-orders', label: '教师订单', icon: ClipboardList },
  { path: '/admin/category-manager', label: '类目与定价', icon: FolderTree },
];

interface MenuChildItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  masterOnly?: boolean;
}

interface MenuItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path?: string;
  children?: MenuChildItem[];
}

const menuItems: MenuItem[] = [
  {
    label: '系统设置',
    icon: Settings,
    children: [
      { path: '/admin/admins', label: '管理员账号', icon: Users, masterOnly: true },
      { path: '/admin/logs', label: '操作日志', icon: ClipboardList },
      { path: '/admin/sms-settings', label: '短信设置', icon: MessageSquare },
      { path: '/admin/ai-settings', label: 'AI配置', icon: Settings, masterOnly: true },
      { path: '/admin/alipay-config', label: '支付宝配置', icon: CreditCard },
      { path: '/admin/scratch-upload', label: '📦 Scratch资源上传(临时)', icon: Upload },
      { path: '/admin/scratch-assets', label: 'Scratch资源库', icon: Package },
      { path: '/admin/point-settings', label: 'U币配置', icon: Settings },
    ]
  },
];

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [loginError, setLoginError] = useState('');
  const [pendingCorrectionsCount, setPendingCorrectionsCount] = useState(0);
  const [pendingTeachersCount, setPendingTeachersCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  const toggleMenu = (label: string) => {
    setExpandedMenus(prev =>
      prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]
    );
  };

  useEffect(() => {
    checkAdminSession();
  }, []);

  // 获取待处理纠错记录数量
  const fetchPendingCorrectionsCount = async () => {
    try {
      const { count, error } = await supabase
        .from('error_corrections')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      if (!error && count !== null) {
        setPendingCorrectionsCount(count);
      }
    } catch (err) {
      console.error('Failed to fetch pending corrections count:', err);
    }
  };

  // 获取待审核教师数量
  const fetchPendingTeachersCount = async () => {
    try {
      const { count, error } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('role', 'teacher')
        .eq('audit_status', 'pending');

      if (!error && count !== null) {
        setPendingTeachersCount(count);
      }
    } catch (err) {
      console.error('Failed to fetch pending teachers count:', err);
    }
  };

  // 设置实时订阅监听纠错记录变化
  useEffect(() => {
    if (!isLoggedIn) return;

    // 初始加载
    fetchPendingCorrectionsCount();
    fetchPendingTeachersCount();

    // 订阅 error_corrections 表的变化
    const correctionsChannel = supabase
      .channel('error-corrections-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'error_corrections',
          filter: 'status=eq.pending'
        },
        () => {
          // 当有变化时重新获取数量
          fetchPendingCorrectionsCount();
        }
      )
      .subscribe();

    // 订阅 profiles 表的变化（教师审核状态）
    const teachersChannel = supabase
      .channel('teacher-audit-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
          filter: 'role=eq.teacher'
        },
        () => {
          // 当有变化时重新获取待审核教师数量
          fetchPendingTeachersCount();
        }
      )
      .subscribe();

    // 清理订阅
    return () => {
      supabase.removeChannel(correctionsChannel);
      supabase.removeChannel(teachersChannel);
    };
  }, [isLoggedIn]);

  const checkAdminSession = async () => {
    const adminSession = localStorage.getItem('admin_session');
    if (adminSession) {
      try {
        const sessionData = JSON.parse(adminSession);
        if (sessionData.username && sessionData.role) {
          const { data: adminData } = await supabase
            .from('admin_users')
            .select('*')
            .eq('username', sessionData.username)
            .eq('status', 'active')
            .maybeSingle();

          if (adminData) {
            const user: AdminUser = {
              id: adminData.id,
              username: adminData.username,
              role: adminData.role as 'super' | 'normal',
              createdAt: adminData.created_at || null
            };
            setCurrentUser(user);
            setIsLoggedIn(true);
            if (location.pathname === '/admin') {
              navigate('/admin/papers');
            }
            return;
          }
        }
      } catch {
        localStorage.removeItem('admin_session');
      }
    }
    setIsLoggedIn(false);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');

    const { data: adminData, error } = await supabase
      .from('admin_users')
      .select('*')
      .eq('username', loginForm.username)
      .eq('password', loginForm.password)
      .eq('status', 'active')
      .maybeSingle();

    if (error || !adminData) {
      setLoginError('账号或密码错误');
      return;
    }

    const sessionData = {
      username: adminData.username,
      role: adminData.role,
      loginTime: Date.now()
    };
    localStorage.setItem('admin_session', JSON.stringify(sessionData));

    const user: AdminUser = {
      id: adminData.id,
      username: adminData.username,
      role: adminData.role as 'super' | 'normal',
      createdAt: adminData.created_at || null
    };
    setCurrentUser(user);
    setIsLoggedIn(true);
    setLoginForm({ username: '', password: '' });

    navigate('/admin/papers');
  };

  const handleLogout = async () => {
    localStorage.removeItem('admin_session');
    setCurrentUser(null);
    setIsLoggedIn(false);
    navigate('/admin');
  };

  const goToFrontend = () => {
    navigate('/exam-categories');
  };

  const visibleMenuItems = menuItems.filter(item => currentUser?.role === 'super');

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600 flex items-center justify-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-2xl p-8 w-96">
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">管理后台</h1>
          </div>
          {loginError && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">{loginError}</div>
          )}
          <form onSubmit={handleLogin} className="space-y-4">
            <input type="text" placeholder="姓名" value={loginForm.username} onChange={e => setLoginForm({ ...loginForm, username: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" />
            <input type="password" placeholder="密码" value={loginForm.password} onChange={e => setLoginForm({ ...loginForm, password: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" />
            <button type="submit" className="w-full py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium shadow-lg transition-all">登录</button>
          </form>
        </motion.div>
      </div>
    );
  }

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <aside className={`${sidebarOpen ? 'w-56' : 'w-0'} bg-gradient-to-b from-blue-900 via-blue-800 to-blue-900 transition-all duration-300 overflow-hidden flex flex-col`}>
        <div className="h-16 flex items-center justify-between px-5 border-b border-blue-700/50">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="font-semibold text-white text-sm">管理后台</span>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="p-1.5 hover:bg-white/10 rounded-lg text-blue-200 hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-5 overflow-y-auto">
          {/* 纠错管理 - 独立一级菜单 */}
          <div>
            <div className="px-3 py-2 flex items-center gap-2">
              <div className="w-1 h-4 bg-orange-400 rounded-full"></div>
              <span className="text-[11px] font-semibold text-blue-200 uppercase tracking-wider">纠错管理</span>
            </div>
            <div className="mt-1 space-y-0.5">
              <Link
                to="/admin/error-correction-review"
                onClick={() => {
                  // 点击后清除通知标识
                  setPendingCorrectionsCount(0);
                }}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 group relative ${
                  isActive('/admin/error-correction-review')
                    ? 'bg-orange-500 text-white shadow-lg shadow-orange-900/30'
                    : 'text-blue-100 hover:bg-white/10 hover:text-white'
                }`}
              >
                <ClipboardList className={`w-4 h-4 transition-colors ${isActive('/admin/error-correction-review') ? 'text-white' : 'text-blue-300 group-hover:text-white'}`} />
                <span className="font-medium">纠错审核</span>
                {isActive('/admin/error-correction-review') ? (
                  <div className="ml-auto w-1.5 h-1.5 bg-white rounded-full"></div>
                ) : pendingCorrectionsCount > 0 && (
                  <div className="ml-auto flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-xs font-bold rounded-full">
                    {pendingCorrectionsCount > 99 ? '99+' : pendingCorrectionsCount}
                  </div>
                )}
              </Link>
              <Link
                to="/admin/teacher-audit"
                onClick={() => {
                  // 点击后清除通知标识
                  setPendingTeachersCount(0);
                }}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 group relative ${
                  isActive('/admin/teacher-audit')
                    ? 'bg-orange-500 text-white shadow-lg shadow-orange-900/30'
                    : 'text-blue-100 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Users className={`w-4 h-4 transition-colors ${isActive('/admin/teacher-audit') ? 'text-white' : 'text-blue-300 group-hover:text-white'}`} />
                <span className="font-medium">教师审核</span>
                {isActive('/admin/teacher-audit') ? (
                  <div className="ml-auto w-1.5 h-1.5 bg-white rounded-full"></div>
                ) : pendingTeachersCount > 0 && (
                  <div className="ml-auto flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-xs font-bold rounded-full">
                    {pendingTeachersCount > 99 ? '99+' : pendingTeachersCount}
                  </div>
                )}
              </Link>
            </div>
          </div>

          {/* 试卷管理区域 */}
          <div>
            <div className="px-3 py-2 flex items-center gap-2">
              <div className="w-1 h-4 bg-blue-400 rounded-full"></div>
              <span className="text-[11px] font-semibold text-blue-200 uppercase tracking-wider">试卷管理</span>
            </div>
            <div className="mt-1 space-y-0.5">
              {paperMenuItems.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 group ${
                    isActive(item.path)
                      ? 'bg-blue-500 text-white shadow-lg shadow-blue-900/30'
                      : 'text-blue-100 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <item.icon className={`w-4 h-4 transition-colors ${isActive(item.path) ? 'text-white' : 'text-blue-300 group-hover:text-white'}`} />
                  <span className="font-medium">{item.label}</span>
                  {isActive(item.path) && (
                    <div className="ml-auto w-1.5 h-1.5 bg-white rounded-full"></div>
                  )}
                </Link>
              ))}
            </div>
          </div>

          {/* 业务管理区域 */}
          <div>
            <div className="px-3 py-2 flex items-center gap-2">
              <div className="w-1 h-4 bg-emerald-400 rounded-full"></div>
              <span className="text-[11px] font-semibold text-blue-200 uppercase tracking-wider">业务管理</span>
            </div>
            <div className="mt-1 space-y-0.5">
              {businessMenuItems.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 group ${
                    isActive(item.path)
                      ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-900/30'
                      : 'text-blue-100 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <item.icon className={`w-4 h-4 transition-colors ${isActive(item.path) ? 'text-white' : 'text-blue-300 group-hover:text-white'}`} />
                  <span className="font-medium">{item.label}</span>
                  {isActive(item.path) && (
                    <div className="ml-auto w-1.5 h-1.5 bg-white rounded-full"></div>
                  )}
                </Link>
              ))}
            </div>
          </div>

          {/* 站内信管理区域 - 可折叠 */}
          <div>
            <button
              onClick={() => toggleMenu('站内信管理')}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200 group ${
                expandedMenus.includes('站内信管理') || messageMenuGroup.items.some(item => isActive(item.path))
                  ? 'bg-amber-500/20 text-white'
                  : 'text-blue-100 hover:bg-white/10 hover:text-white'
              }`}
            >
              <div className="w-1 h-4 bg-amber-400 rounded-full"></div>
              <span className="text-[11px] font-semibold uppercase tracking-wider flex-1 text-left">站内信管理</span>
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${expandedMenus.includes('站内信管理') ? 'rotate-180' : ''}`} />
            </button>
            {(expandedMenus.includes('站内信管理') || messageMenuGroup.items.some(item => isActive(item.path))) && (
              <div className="mt-1 space-y-0.5 ml-4">
                {messageMenuGroup.items.map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 group ${
                      isActive(item.path)
                        ? 'bg-amber-500 text-white shadow-lg shadow-amber-900/30'
                        : 'text-blue-100 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <item.icon className={`w-4 h-4 transition-colors ${isActive(item.path) ? 'text-white' : 'text-blue-300 group-hover:text-white'}`} />
                    <span className="font-medium">{item.label}</span>
                    {isActive(item.path) && (
                      <div className="ml-auto w-1.5 h-1.5 bg-white rounded-full"></div>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* 首页管理区域 - 可折叠 */}
          <div>
            <button
              onClick={() => toggleMenu('首页管理')}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200 group ${
                expandedMenus.includes('首页管理') || homepageMenuGroup.items.some(item => isActive(item.path))
                  ? 'bg-rose-500/20 text-white'
                  : 'text-blue-100 hover:bg-white/10 hover:text-white'
              }`}
            >
              <div className="w-1 h-4 bg-rose-400 rounded-full"></div>
              <span className="text-[11px] font-semibold uppercase tracking-wider flex-1 text-left">首页管理</span>
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${expandedMenus.includes('首页管理') ? 'rotate-180' : ''}`} />
            </button>
            {(expandedMenus.includes('首页管理') || homepageMenuGroup.items.some(item => isActive(item.path))) && (
              <div className="mt-1 space-y-0.5 ml-4">
                {homepageMenuGroup.items.map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 group ${
                      isActive(item.path)
                        ? 'bg-rose-500 text-white shadow-lg shadow-rose-900/30'
                        : 'text-blue-100 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <item.icon className={`w-4 h-4 transition-colors ${isActive(item.path) ? 'text-white' : 'text-blue-300 group-hover:text-white'}`} />
                    <span className="font-medium">{item.label}</span>
                    {isActive(item.path) && (
                      <div className="ml-auto w-1.5 h-1.5 bg-white rounded-full"></div>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* 其他菜单 */}
          <div className="pt-2 border-t border-blue-700/50 space-y-1">
            {visibleMenuItems.map(item => (
              <div key={item.path || item.label}>
                {item.children ? (
                  <div>
                    <button
                      onClick={() => toggleMenu(item.label)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 group ${
                        expandedMenus.includes(item.label)
                          ? 'bg-white/10 text-white'
                          : 'text-blue-100 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <item.icon className={`w-4 h-4 transition-colors ${expandedMenus.includes(item.label) ? 'text-blue-300' : 'text-blue-300 group-hover:text-white'}`} />
                      <span className="font-medium">{item.label}</span>
                      <ChevronDown className={`w-4 h-4 ml-auto transition-transform duration-200 ${expandedMenus.includes(item.label) ? 'rotate-180 text-blue-300' : 'text-blue-400'}`} />
                    </button>
                    {expandedMenus.includes(item.label) && (
                      <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-blue-600/50 pl-3">
                        {item.children.map((child: MenuChildItem) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                              isActive(child.path)
                                ? 'text-blue-300 bg-white/10'
                                : 'text-blue-200 hover:text-white hover:bg-white/5'
                            }`}
                          >
                            <child.icon className="w-3.5 h-3.5" />
                            <span>{child.label}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 group ${
                      isActive(item.path)
                        ? 'bg-white/10 text-white'
                        : 'text-blue-100 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <item.icon className={`w-4 h-4 transition-colors ${isActive(item.path) ? 'text-blue-300' : 'text-blue-300 group-hover:text-white'}`} />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                )}
              </div>
            ))}
          </div>
        </nav>

        {/* 底部用户信息 */}
        <div className="p-3 border-t border-blue-700/50">
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/10">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-semibold">{currentUser?.username?.charAt(0).toUpperCase() || 'A'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{currentUser?.username || '管理员'}</p>
              <p className="text-xs text-blue-200">{currentUser?.role === 'super' ? '超级管理员' : '普通管理员'}</p>
            </div>
            <button onClick={handleLogout} className="p-1.5 hover:bg-white/10 rounded-lg text-blue-200 hover:text-white transition-colors">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>
      <main className="flex-1">
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4">
          <div className="flex items-center gap-4">
            {/* 展开侧边栏按钮 - 仅在侧边栏收起时显示 */}
            {!sidebarOpen && (
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="展开导航栏"
              >
                <Menu className="w-5 h-5" />
              </button>
            )}
            <button onClick={() => navigate('/')} className="flex items-center gap-1.5 px-3 py-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-sm">
              <Home className="w-4 h-4" />首页
            </button>
            <button onClick={goToFrontend} className="flex items-center gap-1.5 px-3 py-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-sm">
              <GraduationCap className="w-4 h-4" />刷题平台
            </button>
            {location.pathname !== '/admin' && location.pathname !== '/admin/' && (
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm"
              >
                <ArrowLeft className="w-4 h-4" />返回
              </button>
            )}
          </div>
          <div className="flex items-center gap-4">
            <span className="text-gray-600">{currentUser?.username}</span>
          </div>
        </header>
        <div className="p-6">
          {location.pathname === '/admin/scratch-upload' ? (
            <iframe
              src="/scratch-upload.html"
              className="w-full h-[calc(100vh-140px)] border-0 rounded-lg"
              title="Scratch资源上传"
            />
          ) : (
            <Outlet />
          )}
        </div>
      </main>
    </div>
  );
}
